/**
 * Audio source selection utilities.
 *
 * Goal: keep existing tab-share flow as the default, and add an optional
 * “virtual cable / microphone device” input mode for VB-CABLE / Voicemeeter.
 */

export const AUDIO_SOURCE_MODE_TAB = "tab";
export const AUDIO_SOURCE_MODE_VIRTUAL_CABLE = "virtualCable";

/** Input sources a Deepgram connect attempt can start on. */
export const STT_SOURCE_TAB = "tab";
export const STT_SOURCE_VIRTUAL_CABLE = "virtualCable";
export const STT_SOURCE_MIC = "mic";

/**
 * Route for a CONNECT attempt (v4.151.1).
 *
 * Mic mode wins over tab/VB, exactly like resolveIdleAudioMode, so the header
 * icon and the Deepgram sockets can never disagree. This is the single place
 * that decides the STT route: the v4.84 regression in useDeepgram ignored the
 * mic flag and opened a tab picker (or VB) instead, so Deepgram never started.
 *
 * @param {{ micTestMode?: boolean, audioSourceMode?: string }} [opts]
 * @returns {'tab'|'virtualCable'|'mic'}
 */
export const resolveSttSource = ({
  micTestMode = false,
  audioSourceMode = AUDIO_SOURCE_MODE_TAB,
} = {}) => {
  if (micTestMode) return STT_SOURCE_MIC;
  if (audioSourceMode === AUDIO_SOURCE_MODE_VIRTUAL_CABLE) return STT_SOURCE_VIRTUAL_CABLE;
  return STT_SOURCE_TAB;
};

export const AUDIO_SOURCE_MODE_KEY = "CATINTASSIST_AUDIO_SOURCE_MODE";
export const VIRTUAL_CABLE_INPUT_DEVICE_ID_KEY =
  "CATINTASSIST_VIRTUAL_CABLE_INPUT_DEVICE_ID";

export const readAudioSourceMode = (storage = typeof localStorage !== "undefined" ? localStorage : null) => {
  try {
    if (!storage) return AUDIO_SOURCE_MODE_TAB;
    const raw = storage.getItem(AUDIO_SOURCE_MODE_KEY);
    if (raw === AUDIO_SOURCE_MODE_VIRTUAL_CABLE) return AUDIO_SOURCE_MODE_VIRTUAL_CABLE;
    return AUDIO_SOURCE_MODE_TAB;
  } catch (_) {
    return AUDIO_SOURCE_MODE_TAB;
  }
};

/**
 * v4.184.0: URL switch for the audio source — `?cable` or `?tab`.
 * Saved like the Settings toggle, so it sticks after reload.
 * Outage use: open http://localhost:3001/?cable → CONNECT uses CABLE Output.
 */
export const applyAudioSourceFromUrl = (search = typeof window !== "undefined" ? window.location.search : "") => {
  const params = new URLSearchParams(search || "");
  if (params.has("cable")) persistAudioSourceMode(AUDIO_SOURCE_MODE_VIRTUAL_CABLE);
  else if (params.has("tab")) persistAudioSourceMode(AUDIO_SOURCE_MODE_TAB);
};

export const persistAudioSourceMode = (
  mode,
  storage = typeof localStorage !== "undefined" ? localStorage : null,
) => {
  try {
    if (!storage) return;
    const next = mode === AUDIO_SOURCE_MODE_VIRTUAL_CABLE ? AUDIO_SOURCE_MODE_VIRTUAL_CABLE : AUDIO_SOURCE_MODE_TAB;
    storage.setItem(AUDIO_SOURCE_MODE_KEY, next);
  } catch (_) {}
};

export const readSelectedVirtualCableInputDeviceId = (
  storage = typeof localStorage !== "undefined" ? localStorage : null,
) => {
  try {
    if (!storage) return "";
    return storage.getItem(VIRTUAL_CABLE_INPUT_DEVICE_ID_KEY) || "";
  } catch (_) {
    return "";
  }
};

export const persistSelectedVirtualCableInputDeviceId = (
  deviceId,
  storage = typeof localStorage !== "undefined" ? localStorage : null,
) => {
  try {
    if (!storage) return;
    storage.setItem(VIRTUAL_CABLE_INPUT_DEVICE_ID_KEY, deviceId || "");
  } catch (_) {}
};

/**
 * Build getUserMedia constraints for VB-CABLE / Voicemeeter-style “machine audio”.
 *
 * IMPORTANT:
 * - echoCancellation/noiseSuppression/autoGainControl must be DISABLED
 * - channelCount must be 1
 * - video must be false
 */
/** STT reads platform audio from CABLE Output / Voicemeeter Output (audioinput). */
export const isVbCableSttInputLabel = (label) => {
  const l = (label || "").toLowerCase();
  if (!l) return false;
  if (l.includes("cable input")) return false;
  if (l.includes("cable output")) return true;
  if (l.includes("voicemeeter output")) return true;
  // v4.104.0: newer Voicemeeter naming — output buses are captured as "Out B1/B2"
  // (virtual call-audio buses). A1-A5 are hardware loopback, not STT sources.
  return /voicemeeter out b[12]\b/.test(l);
};

/** Greetings/mic passthrough go to CABLE Input / Voicemeeter Input (audiooutput). */
export const isVbCableSinkLabel = (label) => {
  const l = (label || "").toLowerCase();
  if (!l) return false;
  if (l.includes("cable output")) return false;
  if (l.includes("cable input")) return true;
  if (/\bcable in\b/.test(l)) return true;
  if (/^voicemeeter\b/.test(l) && /\bin(put)?\b/.test(l)) {
    // Only the standard VAIO endpoint. "In 1" through "In 5" are optional,
    // licensed hardware-input extensions, not aliases for "Input".
    return /^voicemeeter input(\s*\(.*\))?$/.test(l);
  }
  return false;
};

/** v4.104.0: any Voicemeeter playback endpoint — Input, In 1/2+, AUX, VAIO3.
 *  Used for wording + warn tier; full-OK stays Standard/CABLE (isVbCableSinkLabel). */
export const isVoicemeeterInputLabel = (label) => {
  const l = (label || "").toLowerCase();
  return l.startsWith("voicemeeter") && !l.includes("output") && /\bin(put)?\b/.test(l);
};

/**
 * Explicit-pick guard (v4.87.0): once the user hand-picks VB out, auto-fix
 * must never override it (the "selection doesn't stick" bug). The flag is
 * set by changeSinkId and cleared when the device disappears.
 */
export const SINK_EXPLICIT_KEY = "CATINTASSIST_SINK_EXPLICIT";

export const readSinkExplicit = (
  storage = typeof localStorage !== "undefined" ? localStorage : null,
) => {
  try {
    return !!storage && storage.getItem(SINK_EXPLICIT_KEY) === "1";
  } catch (_) {
    return false;
  }
};

export const persistSinkExplicit = (
  on,
  storage = typeof localStorage !== "undefined" ? localStorage : null,
) => {
  try {
    if (!storage) return;
    if (on) storage.setItem(SINK_EXPLICIT_KEY, "1");
    else storage.removeItem(SINK_EXPLICIT_KEY);
  } catch (_) {}
};

/** Pure decision: may the VB-out auto-fix replace the current sink? */
export const shouldAutoFixSink = ({ explicit = false, sinkId = "", sinkLabel = "" } = {}) => {
  if (explicit && sinkId) return false; // user picked it — hands off
  return needsVbCableSinkAutoFix({ sinkId, sinkLabel });
};

export const pickVbCableSttInputDevice = (inputDevices = []) => {
  const match = inputDevices.find((d) => isVbCableSttInputLabel(d.label));
  return match?.deviceId || "";
};

export const pickVbCableSinkDevice = (outputDevices = []) => {
  // v4.104.0: standard "Voicemeeter Input" first (greetings target bus per
  // docs/soundboard/voicemeeter-mic-plan.md), then plain CABLE Input. AUX/VAIO3
  // and numbered extensions are never auto-picked (may not reach the call).
  const lc = (d) => (d.label || "").toLowerCase();
  const not16 = (d) => !/16\s*ch/i.test(d.label || "");
  const find = (re) => outputDevices.find((d) => re.test(lc(d)) && not16(d));
  const match =
    find(/^voicemeeter input(\s*\(.*\))?$/) ||
    find(/\bcable input\b/) ||
    find(/\bcable in\b/);
  return match?.deviceId || "";
};

/** Cable mode: replace missing/wrong VB out (speakers, swapped sides, etc.). */
export const needsVbCableSinkAutoFix = ({ sinkId = "", sinkLabel = "" } = {}) => {
  if (!sinkId) return true;
  const diag = diagnoseVbCableRoute({
    cableMode: true,
    sttInputLabel: "",
    sinkLabel,
    sinkId,
  });
  return !diag.ok && String(diag.code || "").startsWith("sink_");
};

/**
 * Diagnose VB-Cable I/O for greetings + STT.
 * Windows naming: CABLE Input = play into cable; CABLE Output = listen from cable.
 *
 * @returns {{ ok: boolean, code: string, level: 'ok'|'warn'|'err', short: string, tip: string }}
 */
export const diagnoseVbCableRoute = ({
  cableMode = false,
  sttInputLabel = "",
  sinkLabel = "",
  sinkId = "",
} = {}) => {
  if (!cableMode) {
    return {
      ok: true,
      code: "not_cable_mode",
      level: "ok",
      short: "",
      tip: "",
    };
  }

  const sttOk = isVbCableSttInputLabel(sttInputLabel);
  const sinkOk = isVbCableSinkLabel(sinkLabel);
  const sinkLooksLikeSpeakers =
    !!sinkId &&
    !sinkOk &&
    /speaker|headphone|headset|realtek|default|dispositivo|altavoz/i.test(sinkLabel || "");
  const sinkIsCableOutput =
    !!sinkId && /cable\s*output/i.test(sinkLabel || "") && !isVbCableSinkLabel(sinkLabel);

  if (!sinkId) {
    return {
      ok: false,
      code: "sink_missing",
      level: "err",
      short: "VB out empty",
      tip: "🔊 VB out must be CABLE Input or Voicemeeter Input — greetings + mic go into the call's virtual mic.",
    };
  }

  if (sinkIsCableOutput) {
    return {
      ok: false,
      code: "sink_is_cable_output",
      level: "err",
      short: "VB out ≠ Input",
      tip: "You picked CABLE Output (listen). VB out needs CABLE Input (play into). Flip Input↔Output.",
    };
  }

  if (!sinkOk) {
    if (/^voicemeeter in [1-5]\b/i.test(sinkLabel || "")) {
      return {
        ok: false,
        code: "sink_voicemeeter_extension",
        level: "warn",
        short: "Optional Voicemeeter input",
        tip: "Pick Voicemeeter Input (no number) for greetings. Voicemeeter In 1–5 are optional hardware-input extensions that require license/activation; they are not the standard VAIO input.",
      };
    }
    // v4.104.0: Voicemeeter side-bus endpoints (AUX/VAIO3) are real
    // Voicemeeter inputs — warn, don't claim "speakers"; they may not reach B1.
    if (isVoicemeeterInputLabel(sinkLabel)) {
      return {
        ok: false,
        code: "sink_other_bus",
        level: "warn",
        short: "VB out on side bus",
        tip:
          "That Voicemeeter input is a side bus (AUX/VAIO3). Standard Voicemeeter Input (VAIO) is the one wired to B1 — or keep CABLE Input.",
      };
    }
    return {
      ok: false,
      code: sinkLooksLikeSpeakers ? "sink_is_speakers" : "sink_not_cable",
      level: "err",
      short: sinkLooksLikeSpeakers ? "VB out = speakers" : "VB out wrong",
      tip:
        "VB out → CABLE Input / Voicemeeter Input (not speakers). Windows “Listen to this device” only hears greetings if they enter the call's virtual mic. Use 🧪 Test + 🔊 You (Local) to preview on speakers.",
    };
  }

  if (!sttOk && sttInputLabel) {
    const sttIsInput = /cable\s*input/i.test(sttInputLabel);
    return {
      ok: false,
      code: sttIsInput ? "stt_is_cable_input" : "stt_not_cable_output",
      level: "warn",
      short: sttIsInput ? "STT in ≠ Output" : "STT in check",
      tip: sttIsInput
        ? "📥 STT in needs CABLE Output (hear from cable). CABLE Input is for VB out only."
        : "📥 STT in should be CABLE Output so Deepgram hears the call.",
    };
  }

  if (!sttOk) {
    return {
      ok: false,
      code: "stt_missing",
      level: "warn",
      short: "Pick cable in",
      tip: "📥 STT in → CABLE Output. That is the recording side of VB-Cable.",
    };
  }

  return {
    ok: true,
    code: "ok",
    level: "ok",
    short: "Route OK",
    tip: isVoicemeeterInputLabel(sinkLabel)
      ? "Recipe: 📥 CABLE Output · 🎤 real mic · 🔊 Voicemeeter Input. Greetings enter Voicemeeter — confirm that strip routes to B1 (the call mic)."
      : "Recipe: 📥 CABLE Output · 🎤 real mic · 🔊 CABLE Input. Hear yourself: 🧪 Test, or Windows Listen on CABLE Output → headphones.",
  };
};

export const buildVirtualCableGetUserMediaConstraints = (selectedDeviceId) => {
  return {
    audio: {
      deviceId: selectedDeviceId ? { exact: selectedDeviceId } : undefined,
      echoCancellation: false,
      noiseSuppression: false,
      autoGainControl: false,
      channelCount: 1,
    },
    video: false,
  };
};

/**
 * Enumerate input devices and (if device labels are hidden/blank) prompt for
 * temporary audio permission and re-enumerate.
 */
export const refreshInputDevices = async ({
  enumerateDevicesFn,
  getUserMediaFn,
} = {}) => {
  const enumerate = enumerateDevicesFn || navigator.mediaDevices.enumerateDevices.bind(navigator.mediaDevices);
  const getUserMedia = getUserMediaFn || navigator.mediaDevices.getUserMedia.bind(navigator.mediaDevices);

  let devices = await enumerate();
  const inputDevices = devices.filter((d) => d.kind === "audioinput");

  // If labels are blank, request temporary audio permission and re-enumerate.
  const needsPermission = inputDevices.some((d) => !d.label);
  if (needsPermission) {
    try {
      const stream = await getUserMedia({ audio: true, video: false });
      stream.getTracks?.().forEach((t) => t.stop());
    } catch (_) {
      // Best-effort: if permission is denied, keep the blank labels list.
    }
    devices = await enumerate();
  }

  return devices.filter((d) => d.kind === "audioinput");
};

export const buildVirtualCableFailureUiState = (err) => {
  const base =
    "Virtual cable input failed. You can switch back to Tab share.";
  const details = err?.message ? ` (${err.message})` : "";
  return {
    kind: "virtualCableFailure",
    message: `${base}${details}`,
    suggestedActionLabel: "Switch back to Tab share",
  };
};

/**
 * Failure must be “boring and recoverable”.
 * We intentionally do NOT overwrite the stored mode on failure here.
 */
export const getAudioSourceModeAfterVirtualCableFailure = (currentMode) => currentMode;

/** Tab-share needs getDisplayMedia — missing in Cursor preview and some embedded WebViews. */
export const canUseTabCapture = (mediaDevices) => {
  const md = mediaDevices ?? (typeof navigator !== "undefined" ? navigator.mediaDevices : null);
  try {
    return !!(md && typeof md.getDisplayMedia === "function");
  } catch (_) {
    return false;
  }
};

export const isLikelyEmbeddedPreviewBrowser = () => {
  try {
    const ua = typeof navigator !== "undefined" ? navigator.userAgent || "" : "";
    return /CursorBrowser/i.test(ua);
  } catch (_) {
    return false;
  }
};

/** User closed the browser tab-share picker without picking. */
export const isTabCaptureUserCancel = (err) => err?.name === "AbortError";

/** Plain-language tab capture errors for Connect UI. */
export const classifyTabCaptureError = (err) => {
  const name = err?.name || "";
  const embedded = isLikelyEmbeddedPreviewBrowser();

  if (name === "AbortError") {
    return {
      message: "Tab share cancelled — press Connect when ready.",
      suggestMicFallback: false,
      userCancelled: true,
    };
  }

  if (name === "NotSupportedError" || !canUseTabCapture()) {
    return {
      message: embedded
        ? "Tab capture is not available in Cursor's preview browser. Open catintassist.gatrivi.com in Chrome or Edge."
        : "Tab audio capture is not supported in this browser. Use Chrome or Edge.",
      suggestMicFallback: false,
    };
  }
  if (name === "NotAllowedError" || name === "PermissionDeniedError") {
    return {
      message: "Tab sharing was blocked. Allow it in the browser prompt, then choose TAB again.",
      suggestMicFallback: false,
    };
  }
  return {
    message: err?.message
      ? `Tab capture failed: ${err.message}`
      : "Tab capture failed. Try TAB again or open the app in Chrome/Edge.",
    suggestMicFallback: false,
  };
};

